/* eslint-disable no-console */
/* global newrelic */
import { v4 } from "uuid";
import Cookies from "js-cookie";

import { browserName, deviceType, osName } from "utils/deviceDetectUtils";

export const MOBILE_DEBUG_COOKIE_NAME = "mobileDebugOn";

export const mobileDebugOn = () => Cookies.get(MOBILE_DEBUG_COOKIE_NAME) === "true";

export const newRelic = (msg, err) => {
  if (typeof newrelic === "undefined") return;
  if (msg instanceof Error) {
    newrelic.noticeError(msg, err);

    return;
  }
  newrelic.noticeError(new Error(String(msg)), err);
};

const formatMessageForNewRelic = (message) => {
  if (message == null) return "";
  if (typeof message === "string") return message;
  if (message instanceof Error) return message.stack || message.message || String(message);
  if (typeof message === "object") {
    try {
      return JSON.stringify(message);
    } catch {
      return String(message);
    }
  }
  return String(message);
};

const DEFAULT_STYLE_LOGS = {
  margin: "0",
  "z-index": 999999,
  width: "100%",
  position: "relative",
  background: "white",
};

const getUserInfo = () => {
  if (!window.currentUser) return null;

  return {
    userId: window.currentUser.id,
    isAnonymousUser: window.currentUser.role === "anonymous",
  };
};

const getBrowserInfo = () => {
  const device = deviceType === "browser" ? "desktop" : deviceType;
  const cookieName = "nr_logger_browser_id";
  const legacyCookieName = "sumo_logger_browser_id";
  let browserId = Cookies.get(cookieName) || Cookies.get(legacyCookieName);

  if (!browserId) {
    browserId = v4();
  }

  Cookies.set(cookieName, browserId, { expires: 1825 });
  Cookies.set(legacyCookieName, browserId, { expires: 1825 });

  return {
    browserId,
    browserName,
    osName,
    device,
  };
};

const getTrackingAttributes = () => {
  const browserInfo = getBrowserInfo();
  const userInfo = getUserInfo();

  return {
    browserId: browserInfo.browserId,
    browserName: browserInfo.browserName,
    osName: browserInfo.osName,
    device: browserInfo.device,
    userId: userInfo?.userId,
    isAnonymousUser: userInfo?.isAnonymousUser,
  };
};

const mobileDebug = (message) => {
  if (!mobileDebugOn()) return;

  const elem = document.createElement("p");
  Object.keys(DEFAULT_STYLE_LOGS).forEach((property) => {
    if (property != null) elem.style[property] = DEFAULT_STYLE_LOGS[property];
  });

  elem.innerHTML = message;
  document.body.appendChild(elem);
};

const normalizeCustomAttributes = (obj) => {
  const out = {};
  Object.entries(obj).forEach(([key, value]) => {
    if (value === null || value === undefined) return;
    out[key] = typeof value === "object" ? JSON.stringify(value) : value;
  });
  return out;
};

const sendToNewRelic = (message, level, options) => {
  if (typeof newrelic === "undefined") return;

  const nrMessage = formatMessageForNewRelic(message);
  const customAttributes = normalizeCustomAttributes({
    ...getTrackingAttributes(),
    url: window.location.href,
    ...options,
  });

  if (typeof newrelic.log === "function") {
    newrelic.log(nrMessage, { level, customAttributes });
    mobileDebug(nrMessage);

    return;
  }

  if (typeof newrelic.addPageAction === "function") {
    newrelic.addPageAction("jj_app_log", {
      level,
      message: nrMessage,
      ...customAttributes,
    });
    mobileDebug(nrMessage);

    return;
  }

  console.error(`New Relic logging unavailable — intended (${level}): ${nrMessage}`);
  mobileDebug(nrMessage);
};

export const jjLogger = {
  log(message, options) {
    console.log(message);
    sendToNewRelic(message, "info", options);
  },

  logError(message, options) {
    if (typeof message === "string" && message.includes("ResizeObserver loop completed with undelivered notifications"))
      return;

    console.error(message);
    newRelic(message);
    sendToNewRelic(message, "error", options);
  },

  logDebug(message) {
    console.debug(message);
    mobileDebug(message);
  },
};
